import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { MapPin, Heart, Calendar, Info, Share2, MessageCircle, Bookmark, BookmarkCheck } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { copyToClipboard } from '@/lib/utils';

interface AdoptionPost {
  id: string;
  title: string;
  pet_name: string;
  species: string;
  breed?: string;
  age?: number;
  size?: string;
  gender?: string;
  character_traits?: string;
  health_info?: string;
  description?: string;
  location: string;
  adoption_conditions?: string;
  image_url?: string;
  status: string;
  requests_count: number;
  created_at: string;
  user_id: string;
  profiles?: {
    user_id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface PublicAdoptionCardProps {
  post: AdoptionPost;
  onRequestAdoption: (post: AdoptionPost) => void;
  onContactOwner?: (post: AdoptionPost) => void;
  currentUserId?: string;
  onSaveChange?: () => void; // Callback per aggiornare la lista delle adozioni salvate
}

export default function PublicAdoptionCard({ post, onRequestAdoption, onContactOwner, currentUserId, onSaveChange }: PublicAdoptionCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savingPost, setSavingPost] = useState(false);
  const { toast } = useToast();
  const isOwner = currentUserId === post.user_id;

  // Check if adoption post is saved
  useEffect(() => {
    const checkIfSaved = async () => {
      if (!currentUserId) return;
      
      try {
        const { data } = await supabase
          .from('adoption_saves')
          .select('id')
          .eq('adoption_post_id', post.id)
          .eq('user_id', currentUserId)
          .maybeSingle();
        
        setIsSaved(!!data);
      } catch (error) {
        console.error('Error checking if adoption is saved:', error);
      }
    };
    
    checkIfSaved();
  }, [post.id, currentUserId]);

  const getAgeText = (age?: number) => {
    if (!age) return '';
    return age === 1 ? '1 anno' : `${age} anni`;
  };

  const handleContactOwner = (post: AdoptionPost) => {
    if (onContactOwner) {
      onContactOwner(post);
    } else {
      toast({
        title: "Contatto non disponibile",
        description: "Non è possibile contattare questo utente al momento",
        variant: "destructive",
      });
    }
  };

  const handleSaveAdoption = async () => {
    if (!currentUserId) {
      toast({
        title: "Accesso richiesto",
        description: "Effettua l'accesso per salvare gli annunci di adozione",
        variant: "destructive",
      });
      return;
    }

    if (savingPost) return;
    setSavingPost(true);

    try {
      if (isSaved) {
        // Remove from saved
        const { error } = await supabase
          .from('adoption_saves')
          .delete()
          .eq('adoption_post_id', post.id)
          .eq('user_id', currentUserId);
        
        if (error) throw error;
        
        setIsSaved(false);
        toast({
          title: "Rimosso dai salvati",
          description: "L'annuncio di adozione è stato rimosso dai tuoi salvati",
        });
        
        // Notifica il parent component del cambiamento
        if (onSaveChange) onSaveChange();
      } else {
        // Add to saved
        const { error } = await supabase
          .from('adoption_saves')
          .insert({
            adoption_post_id: post.id,
            user_id: currentUserId
          });
        
        if (error) throw error;
        
        setIsSaved(true);
        toast({
          title: "Salvato!",
          description: "L'annuncio di adozione è stato aggiunto ai tuoi salvati",
        });
        
        // Notifica il parent component del cambiamento
        if (onSaveChange) onSaveChange();
      }
    } catch (error) {
      console.error('Error saving adoption post:', error);
      toast({
        title: "Errore",
        description: "Impossibile salvare l'annuncio di adozione",
        variant: "destructive",
      });
    } finally {
      setSavingPost(false);
    }
  };

  const handleShare = async () => {
    try {
      const adoptionUrl = `${window.location.origin}/app/adoption/${post.id}`;
      
      if (navigator.share) {
        await navigator.share({
          title: `${post.title} - ${post.pet_name}`,
          text: `${post.pet_name} cerca una nuova casa! ${post.description ? post.description.substring(0, 100) + '...' : ''}`,
          url: adoptionUrl,
        });
      } else {
        const success = await copyToClipboard(adoptionUrl);
        if (success) {
          toast({
            title: "Link copiato!",
            description: "Il link dell'annuncio è stato copiato negli appunti",
          });
        } else {
          toast({
            title: "Errore",
            description: "Impossibile copiare il link",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Error sharing adoption post:', error);
    }
  };

  return (
    <>
      <Card className="glass border-white/20 hover:border-white/30 transition-colors overflow-hidden">
        <div className="relative">
          {post.image_url && (
            <div className="aspect-video w-full overflow-hidden">
              <img 
                src={post.image_url} 
                alt={post.pet_name}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          )}
          <div className="absolute top-3 left-3">
            <Badge className="bg-green-500/20 text-green-700 border-green-500/20">
              Disponibile
            </Badge>
          </div>
        </div>

        <CardHeader className="pb-3">
          <div className="space-y-3">
            <h3 className="font-semibold text-white text-lg">{post.title}</h3>
            
            {/* Author Info */}
            <div className="flex items-center space-x-2">
              <Avatar className="w-6 h-6">
                <AvatarImage src={post.profiles?.avatar_url || ''} />
                <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                  {post.profiles?.display_name ? 
                    post.profiles.display_name.charAt(0).toUpperCase() : 
                    post.profiles?.username?.charAt(0).toUpperCase() || 'U'
                  }
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-muted-foreground">
                Pubblicato da {post.profiles?.display_name || post.profiles?.username || 'Utente'}
              </span>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <span className="font-medium text-white">{post.pet_name}</span>
              <span>•</span>
              <span>{post.species}</span>
              {post.breed && (
                <>
                  <span>•</span>
                  <span>{post.breed}</span>
                </>
              )}
            </div>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              {post.age && (
                <span>{getAgeText(post.age)}</span>
              )}
              {post.size && (
                <>
                  <span>•</span>
                  <span>Taglia {post.size.toLowerCase()}</span>
                </>
              )}
              {post.gender && (
                <>
                  <span>•</span>
                  <span>{post.gender}</span>
                </>
              )}
            </div>
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span>{post.location}</span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {post.description && (
            <div className="p-3 bg-muted/10 rounded-lg border border-white/10">
              <p className="text-sm text-muted-foreground line-clamp-2">
                {post.description}
              </p>
            </div>
          )}

          {/* Action buttons - Contained within card */}
          <div className="space-y-3 w-full overflow-hidden">
            {/* Primary action buttons */}
            {!isOwner && (
              <div className="flex justify-center gap-2 w-full max-w-full overflow-hidden">
                <Button
                  onClick={() => onRequestAdoption(post)}
                  size="sm"
                  className="flex-1 max-w-[120px] h-9 bg-primary hover:bg-primary/90 text-xs font-medium px-2"
                >
                  <Heart className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate min-w-0">Adotta</span>
                </Button>
                <Button
                  onClick={() => handleContactOwner(post)}
                  size="sm"
                  variant="secondary"
                  className="flex-1 max-w-[120px] h-9 border-primary/20 hover:border-primary/40 text-xs font-medium px-2"
                >
                  <MessageCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate min-w-0">Contatta</span>
                </Button>
                <Button
                  onClick={handleSaveAdoption}
                  disabled={savingPost}
                  size="sm"
                  variant="outline"
                  className={`flex-1 max-w-[120px] h-9 text-xs font-medium px-2 ${
                    isSaved 
                      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20' 
                      : 'border-muted-foreground/30 hover:border-primary/40'
                  }`}
                >
                  {isSaved ? <BookmarkCheck className="w-3 h-3 mr-1 flex-shrink-0" /> : <Bookmark className="w-3 h-3 mr-1 flex-shrink-0" />}
                  <span className="truncate min-w-0">{isSaved ? 'Salvato' : 'Salva'}</span>
                </Button>
              </div>
            )}
            
            {/* Secondary actions - Centered */}
            <div className="flex justify-center gap-2 max-w-full overflow-hidden">
              <Button
                onClick={() => setShowDetails(true)}
                size="sm"
                variant="outline"
                className="flex-1 max-w-[140px] h-9 border-primary/30 hover:bg-primary/10 text-xs font-medium px-3"
              >
                <Info className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate min-w-0">Dettagli</span>
              </Button>
              <Button
                onClick={() => handleShare()}
                size="sm"
                variant="outline"
                className="flex-1 max-w-[140px] h-9 border-primary/30 hover:bg-primary/10 text-xs font-medium px-3"
              >
                <Share2 className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate min-w-0">Condividi</span>
              </Button>
            </div>
          </div>

          <div className="text-xs text-muted-foreground flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Calendar className="w-3 h-3" />
              <span>Pubblicato {format(new Date(post.created_at), 'dd MMM', { locale: it })}</span>
            </div>
            {post.requests_count > 0 && (
              <span>{post.requests_count} richieste</span>
            )}
          </div>

          <div className="text-xs text-muted-foreground flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Calendar className="w-3 h-3" />
              <span>Pubblicato {format(new Date(post.created_at), 'dd MMM', { locale: it })}</span>
            </div>
            {post.requests_count > 0 && (
              <span>{post.requests_count} richieste</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{post.title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {post.image_url && (
              <div className="aspect-video w-full rounded-lg overflow-hidden">
                <img 
                  src={post.image_url} 
                  alt={post.pet_name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Nome:</strong> {post.pet_name}
              </div>
              <div>
                <strong>Specie:</strong> {post.species}
              </div>
              {post.breed && (
                <div>
                  <strong>Razza:</strong> {post.breed}
                </div>
              )}
              {post.age && (
                <div>
                  <strong>Età:</strong> {getAgeText(post.age)}
                </div>
              )}
              {post.size && (
                <div>
                  <strong>Taglia:</strong> {post.size}
                </div>
              )}
              {post.gender && (
                <div>
                  <strong>Sesso:</strong> {post.gender}
                </div>
              )}
              <div className="col-span-2">
                <strong>Zona:</strong> {post.location}
              </div>
            </div>

            {post.character_traits && (
              <div>
                <strong className="block mb-2">Carattere:</strong>
                <p className="text-muted-foreground">{post.character_traits}</p>
              </div>
            )}

            {post.health_info && (
              <div>
                <strong className="block mb-2">Salute:</strong>
                <p className="text-muted-foreground">{post.health_info}</p>
              </div>
            )}

            {post.description && (
              <div>
                <strong className="block mb-2">Descrizione:</strong>
                <p className="text-muted-foreground">{post.description}</p>
              </div>
            )}

            {post.adoption_conditions && (
              <div>
                <strong className="block mb-2">Condizioni di Adozione:</strong>
                <p className="text-muted-foreground">{post.adoption_conditions}</p>
              </div>
            )}

            {!isOwner && (
              <div className="flex justify-center gap-3 mt-6">
                <Button
                  onClick={() => {
                    setShowDetails(false);
                    onRequestAdoption(post);
                  }}
                  size="default"
                  className="flex-1 min-w-[120px] max-w-[160px] h-11 bg-primary hover:bg-primary/90 text-sm font-medium px-4"
                >
                  <Heart className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Adotta</span>
                </Button>
                <Button
                  onClick={() => {
                    setShowDetails(false);
                    handleContactOwner(post);
                  }}
                  size="default"
                  variant="secondary"
                  className="flex-1 min-w-[120px] max-w-[160px] h-11 border-primary/20 hover:border-primary/40 text-sm font-medium px-4"
                >
                  <MessageCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Contatta</span>
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}